// Turn a userfile's structured `calibratability` (set by userFilesRoutes from
// calibrationService.checkFileCalibratabilityFromUserFile) into a "stale" chip
// label + tooltip lines. A calibratable file whose only matching REQUIRED
// master is a live-fallback to an expired/superseded one — the server reports
// this per type as `{ available: true, stale: true }`. The file still
// calibrates (and "Download Calibrated" still produces a calibrated file), so
// this is a WARNING, not "No Cal". Companion to missingCalibrationReason.js:
// the two are mutually exclusive (missing fires only when calibratable=false,
// stale only when calibratable=true), which is what lets a single component
// render missing → stale → clean in precedence.

/**
 * @param {object} file  UserFile from the /user-files endpoints — needs at
 *   least { calibratable, calibratability, filter, exposure_time }.
 * @returns {{ label: string, color: 'info', tooltipLines: string[] } | null}
 *   null unless the file is calibratable AND some required master is stale.
 */
export function staleCalibrationReason(file) {
    if (!file) return null;
    // Only a calibratable file can be "stale-calibratable". A non-calibratable
    // file is "No Cal" (missingCalibrationReason), never stale.
    if (file.calibratable !== true) return null;
    const cal = file.calibratability;
    if (!cal) return null;

    const stale = [];

    const addLine = (type, info) => {
        // Mirror missingCalibrationReason's required-gating: a non-required
        // prerequisite (e.g. bias on CMOS) is reported { required: false } and
        // must never be surfaced. `stale` is only meaningful when available.
        if (!info || !info.required) return;
        if (!info.stale) return;
        if (type === 'flat' && file.filter) {
            stale.push(`flat for filter ${file.filter}`);
        } else if (type === 'dark' && file.exposure_time != null) {
            stale.push(`dark master (${file.exposure_time}s)`);
        } else {
            stale.push(`${type} master`);
        }
    };

    addLine('dark', cal.dark);
    addLine('flat', cal.flat);
    addLine('bias', cal.bias);

    if (stale.length === 0) return null;

    return {
        label: 'Cal · stale',
        color: 'info',
        tooltipLines: [
            `Calibrated with an expired master: ${stale.join(', ')}.`,
            `A fresh master is being (or needs to be) built; this clears automatically once it is available.`,
        ],
    };
}
